import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import usePortal from 'common/hooks/usePortal';
import './ClampedCell.scss';

export const DEFAULT_MAX_LINES = 4;
// Gap between the cell and the tooltip, and the minimum gap to the viewport edges.
export const TOOLTIP_OFFSET = 10;
export const VIEWPORT_MARGIN = 10;

// Opens below the cell unless it doesn't fit there and there's more room above.
export function tooltipPosition(anchorRect, tooltipRect, viewport) {
  const spaceBelow = viewport.height - anchorRect.bottom - TOOLTIP_OFFSET;
  const spaceAbove = anchorRect.top - TOOLTIP_OFFSET;
  const placeAbove = tooltipRect.height > spaceBelow && spaceAbove > spaceBelow;
  const centeredLeft = anchorRect.left + ((anchorRect.width - tooltipRect.width) / 2);
  const maxLeft = viewport.width - tooltipRect.width - VIEWPORT_MARGIN;

  return {
    placement: placeAbove ? 'top' : 'bottom',
    top: placeAbove ?
      anchorRect.top - tooltipRect.height - TOOLTIP_OFFSET :
      anchorRect.bottom + TOOLTIP_OFFSET,
    left: Math.max(VIEWPORT_MARGIN, Math.min(centeredLeft, maxLeft)),
  };
}

const ClampedCell = ({ children, maxLines }) => {
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const tooltipRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const showTooltip = isClamped && tooltipOpen;

  const checkClamped = useCallback(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }

    setIsClamped(el.scrollHeight - el.clientHeight > 1);
  }, []);

  useEffect(() => {
    checkClamped();
  }, [children, maxLines, checkClamped]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new ResizeObserver(checkClamped);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkClamped]);

  useEffect(() => {
    if (!tooltipOpen) {
      return undefined;
    }
    const close = () => setTooltipOpen(false);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    const onMouseDown = (event) => {
      const inCell = wrapperRef.current && wrapperRef.current.contains(event.target);
      const inTooltip = tooltipRef.current && tooltipRef.current.contains(event.target);
      if (!inCell && !inTooltip) {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', close, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', close, true);
    };
  }, [tooltipOpen]);

  useLayoutEffect(() => {
    if (!showTooltip) {
      setPosition(null);
      return;
    }
    if (!contentRef.current || !tooltipRef.current) {
      return;
    }
    setPosition(tooltipPosition(
      contentRef.current.getBoundingClientRect(),
      tooltipRef.current.getBoundingClientRect(),
      { width: window.innerWidth, height: window.innerHeight },
    ));
  }, [showTooltip, children]);

  // Portaled so it isn't trapped below page overlays by the table's isolated
  // stacking context. Clicks on it must not reach the row's click handler.
  const tooltip = usePortal(showTooltip ? (
    <div
      ref={tooltipRef}
      role="tooltip"
      className="fixed z-popover border border-solid p-4 border-light-border-blue rounded bg-white"
      data-placement={position ? position.placement : undefined}
      style={{
        top: position ? position.top : 0,
        left: position ? position.left : 0,
        visibility: position ? 'visible' : 'hidden',
        maxWidth: '300px',
        wordWrap: 'break-word',
        whiteSpace: 'normal',
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  ) : null, showTooltip);

  return (
    <div
      ref={wrapperRef}
      className="dynamic-table__clamp-wrapper"
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
    >
      <div>
        <div
          ref={contentRef}
          className="dynamic-table__clamp"
          style={{ '--dynamic-table-clamp-lines': maxLines }}
        >
          {children}
        </div>
      </div>
      {tooltip}
    </div>
  );
};

ClampedCell.propTypes = {
  children: PropTypes.node,
  maxLines: PropTypes.number,
};

ClampedCell.defaultProps = {
  children: null,
  maxLines: DEFAULT_MAX_LINES,
};

export default ClampedCell;
